<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <!-- Header -->
    <div
      :class="[
        theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
        'border-b px-6 py-4'
      ]"
    >
      <div class="flex items-center justify-between">
        <div>
          <h1
            :class="[
              theme.isDark.value ? 'text-gray-100' : 'text-gray-900',
              'text-2xl font-bold'
            ]"
          >
            Audit Logs
          </h1>
          <p
            :class="[
              theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
              'mt-1 text-sm'
            ]"
          >
            View and filter platform audit logs
          </p>
        </div>
        <div class="flex items-center space-x-3">
          <!-- Refresh Button -->
          <button
            @click="refreshLogs"
            :disabled="loading"
            :class="[
              theme.isDark.value
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                : 'bg-white hover:bg-gray-50 text-gray-700',
              'px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium transition-colors',
              loading ? 'opacity-50 cursor-not-allowed' : ''
            ]"
          >
            <svg
              :class="[
                loading ? 'animate-spin' : '',
                'w-4 h-4 mr-2'
              ]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {{ loading ? 'Loading...' : 'Refresh' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="p-6 space-y-6">
      <!-- Filters -->
      <AuditLogsFilters
        v-model:filters="filters"
        :resource-types="resourceTypes"
        @apply-filters="handleApplyFilters"
      />

      <!-- Error Message -->
      <div
        v-if="error"
        :class="[
          theme.isDark.value ? 'bg-red-900 border-red-700' : 'bg-red-50 border-red-200',
          'border rounded-md p-4'
        ]"
      >
        <div class="flex">
          <div class="flex-shrink-0">
            <svg
              class="h-5 w-5 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800 dark:text-red-200">
              Error loading audit logs
            </h3>
            <div class="mt-2 text-sm text-red-700 dark:text-red-300">
              {{ error }}
            </div>
            <div class="mt-4">
              <button
                @click="refreshLogs"
                :class="[
                  theme.isDark.value
                    ? 'bg-red-800 hover:bg-red-700 text-red-100'
                    : 'bg-red-100 hover:bg-red-200 text-red-800',
                  'px-3 py-2 rounded-md text-sm font-medium transition-colors'
                ]"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div
        v-if="loading && auditLogs.length === 0"
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-lg p-8'
        ]"
      >
        <div class="flex items-center justify-center">
          <svg
            class="animate-spin h-8 w-8 text-blue-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          <span
            :class="[
              theme.isDark.value ? 'text-gray-300' : 'text-gray-600',
              'ml-3 text-lg'
            ]"
          >
            Loading audit logs...
          </span>
        </div>
      </div>

      <!-- Audit Logs Table -->
      <div v-else>
        <AuditLogsTable
          :audit-logs="auditLogs"
          :loading="loading"
        />
      </div>

      <!-- Load More Button -->
      <div
        v-if="pagination.hasMore && !loading"
        class="flex justify-center"
      >
        <button
          @click="loadMore"
          :class="[
            theme.isDark.value
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
              : 'bg-white hover:bg-gray-50 text-gray-700',
            'px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium transition-colors'
          ]"
        >
          Load More
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, inject } from 'vue'
import { useTheme } from '../../utils/useTheme'
import AuditLogsFilters from './components/AuditLogsFilters.vue'
import AuditLogsTable from './components/AuditLogsTable.vue'
import { auditLogsService } from '../../services/audit/auditLogsService'
import type { AuditLog, AuditLogsFilters as AuditLogsFiltersType, AuditLogsQueryParams } from '../../types/audit-logs'
import { logger } from '../../utils/logger'

// Use theme via inject with a fallback
const theme = inject('theme', useTheme())

// State
const auditLogs = ref<AuditLog[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const resourceTypes = ref<string[]>([])

// Filters
const filters = reactive<AuditLogsFiltersType>({
  resourceType: '',
  resourceId: '',
  userId: '',
  startDate: '',
  endDate: '',
  httpMethod: []
})

// Pagination
const pagination = reactive({
  total: 0,
  hasMore: false,
  nextCursor: undefined as string | undefined
})

// Methods
const fetchAuditLogs = async (params: AuditLogsQueryParams = {}, append = false) => {
  try {
    loading.value = true
    error.value = null

    logger.log('Fetching audit logs with params:', params)

    const response = await auditLogsService.fetchAuditLogs(params)
    
    if (append) {
      auditLogs.value.push(...response.data)
    } else {
      auditLogs.value = response.data
    }

    pagination.total = response.pagination.total
    pagination.hasMore = response.pagination.hasMore
    pagination.nextCursor = response.pagination.nextCursor

    // Extract unique resource types for filter dropdown
    const types = new Set(auditLogs.value.map(log => log.resource.type))
    resourceTypes.value = Array.from(types).sort()

    logger.log('Audit logs fetched successfully:', {
      count: response.data.length,
      total: response.pagination.total,
      hasMore: response.pagination.hasMore
    })
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to fetch audit logs'
    logger.error('Error fetching audit logs:', err)
  } finally {
    loading.value = false
  }
}

const refreshLogs = () => {
  const params = buildQueryParams()
  fetchAuditLogs(params, false)
}

const loadMore = () => {
  if (!pagination.nextCursor) return
  
  const params = buildQueryParams()
  params.cursor = pagination.nextCursor
  fetchAuditLogs(params, true)
}

const handleApplyFilters = () => {
  const params = buildQueryParams()
  fetchAuditLogs(params, false)
}

const buildQueryParams = (): AuditLogsQueryParams => {
  const params: AuditLogsQueryParams = {
    limit: 50
  }

  if (filters.resourceType) params.resourceType = filters.resourceType
  if (filters.resourceId) params.resourceId = filters.resourceId
  if (filters.userId) params.userId = filters.userId
  if (filters.startDate) params.startDate = new Date(filters.startDate).toISOString()
  if (filters.endDate) params.endDate = new Date(filters.endDate).toISOString()

  return params
}

// Lifecycle
onMounted(() => {
  fetchAuditLogs()
})
</script>
